import { useEffect, useMemo, useState } from 'react';
import { Plus, Edit, Trash2, GripVertical, ArrowLeft, X, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { usePracticeTypeStore } from '@/store/practiceTypeStore';
import { useActivityTypeStore } from '@/store/activityTypeStore';
import { toast } from 'sonner';
import { DefaultActivity, DefaultAppointment } from '@/services/practiceTypeService';

const FOLDER_TEMPLATE_DEFAULT = '{practiceNumber} - {clientName} - {interventionLocation} - {practiceTypeName}';

type InvalidDefaultActivityDetail = {
  index: number;
  reason: string;
  detail?: string;
  normalized?: string;
  received?: string;
};

function toastPracticeTypeValidationError(error: unknown, fallback: string) {
  const err = error as {
    response?: { data?: { message?: string; details?: { invalid?: InvalidDefaultActivityDetail[] } } };
  };
  const data = err.response?.data;
  const message = data?.message ?? fallback;
  const invalid = data?.details?.invalid;
  if (Array.isArray(invalid) && invalid.length > 0) {
    const desc = invalid
      .map((i) => {
        const label =
          i.reason === 'INVALID_CHARS' ? 'caratteri non permessi' : 'codice tipo non valido o non attivo';
        const extra = [i.detail, i.normalized ? `codice: “${i.normalized}”` : null].filter(Boolean).join(' — ');
        return `Riga ${i.index + 1}: ${label}${extra ? ` — ${extra}` : ''}`;
      })
      .join('\n');
    toast.error(message, { description: desc, duration: 12000 });
  } else {
    toast.error(message);
  }
}

export default function PracticeTypesPage() {
  const navigate = useNavigate();
  const {
    practiceTypes,
    loading,
    error,
    fetchPracticeTypes,
    createPracticeType,
    updatePracticeType,
    deletePracticeType,
    reorderPracticeTypes,
  } = usePracticeTypeStore();

  const { activityTypes, fetchActivityTypes } = useActivityTypeStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingType, setEditingType] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    icon: '',
    color: '',
    isActive: true,
    order: 0,
    defaultActivities: [] as DefaultActivity[],
    defaultAppointments: [] as DefaultAppointment[],
    folderParentTemplate: FOLDER_TEMPLATE_DEFAULT,
    folderSubfolders: [] as string[],
  });

  useEffect(() => {
    fetchPracticeTypes(false);
    fetchActivityTypes(true); // Carica tipi attività dal database
  }, [fetchPracticeTypes, fetchActivityTypes]);

  const validateDefaultActivities = (): boolean => {
    const incomplete = formData.defaultActivities.some(a => !a.type);
    if (incomplete) {
      toast.error('Seleziona il tipo per ogni attività predefinita prima di salvare.');
      return false;
    }
    return true;
  };

  const handleCreate = async () => {
    if (!validateDefaultActivities()) return;
    try {
      const dataToSend = {
        ...formData,
        defaultActivities: formData.defaultActivities.length > 0 ? formData.defaultActivities : null,
        defaultAppointments: formData.defaultAppointments.length > 0 ? formData.defaultAppointments : null,
        folderParentTemplate: (formData.folderParentTemplate || FOLDER_TEMPLATE_DEFAULT).trim() || null,
        folderSubfolders: formData.folderSubfolders.length > 0 ? formData.folderSubfolders : null,
      };
      await createPracticeType(dataToSend);
      setShowCreateModal(false);
      setFormData({
        name: '',
        code: '',
        description: '',
        icon: '',
        color: '',
        isActive: true,
        order: practiceTypes.length,
        defaultActivities: [],
        defaultAppointments: [],
        folderParentTemplate: FOLDER_TEMPLATE_DEFAULT,
        folderSubfolders: [],
      });
      toast.success('Tipo pratica creato con successo');
    } catch (error: any) {
      toastPracticeTypeValidationError(error, 'Errore nella creazione');
    }
  };

  const handleEdit = (type: any) => {
    setEditingType(type);
    console.log('Loading practice type for edit:', JSON.stringify(type, null, 2));
    console.log('defaultActivities:', type.defaultActivities, typeof type.defaultActivities);
    console.log('defaultAppointments:', type.defaultAppointments, typeof type.defaultAppointments);
    
    // Parse JSON fields if they are strings
    let parsedDefaultActivities: DefaultActivity[] = [];
    if (type.defaultActivities) {
      if (typeof type.defaultActivities === 'string') {
        try {
          parsedDefaultActivities = JSON.parse(type.defaultActivities);
        } catch (e) {
          console.error('Error parsing defaultActivities:', e);
          parsedDefaultActivities = [];
        }
      } else if (Array.isArray(type.defaultActivities)) {
        parsedDefaultActivities = type.defaultActivities;
      }
    }
    
    let parsedDefaultAppointments: DefaultAppointment[] = [];
    if (type.defaultAppointments) {
      if (typeof type.defaultAppointments === 'string') {
        try {
          parsedDefaultAppointments = JSON.parse(type.defaultAppointments);
        } catch (e) {
          console.error('Error parsing defaultAppointments:', e);
          parsedDefaultAppointments = [];
        }
      } else if (Array.isArray(type.defaultAppointments)) {
        parsedDefaultAppointments = type.defaultAppointments;
      }
    }
    
    setFormData({
      name: type.name,
      code: type.code,
      description: type.description || '',
      icon: type.icon || '',
      color: type.color || '',
      isActive: type.isActive,
      order: type.order,
      defaultActivities: parsedDefaultActivities,
      defaultAppointments: parsedDefaultAppointments,
      folderParentTemplate: type.folderParentTemplate || FOLDER_TEMPLATE_DEFAULT,
      folderSubfolders: Array.isArray(type.folderSubfolders) ? type.folderSubfolders : [],
    });
    setShowCreateModal(true);
  };

  const handleUpdate = async () => {
    if (!editingType) return;
    if (!validateDefaultActivities()) return;
    try {
      const dataToSend: any = {
        name: formData.name,
        code: formData.code,
        description: formData.description,
        icon: formData.icon,
        color: formData.color,
        isActive: formData.isActive,
        order: formData.order,
        defaultActivities: formData.defaultActivities.length > 0 ? formData.defaultActivities : null,
        defaultAppointments: formData.defaultAppointments.length > 0 ? formData.defaultAppointments : null,
        folderParentTemplate: (formData.folderParentTemplate || FOLDER_TEMPLATE_DEFAULT).trim() || null,
        folderSubfolders: formData.folderSubfolders.length > 0 ? formData.folderSubfolders : null,
      };
      console.log('Sending update data:', JSON.stringify(dataToSend, null, 2));
      await updatePracticeType(editingType.id, dataToSend);
      await fetchPracticeTypes(false);
      setShowCreateModal(false);
      setEditingType(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        icon: '',
        color: '',
        isActive: true,
        order: 0,
        defaultActivities: [],
        defaultAppointments: [],
        folderParentTemplate: FOLDER_TEMPLATE_DEFAULT,
        folderSubfolders: [],
      });
      toast.success('Tipo pratica aggiornato con successo');
    } catch (error: any) {
      toastPracticeTypeValidationError(error, "Errore nell'aggiornamento");
    }
  };

  const addDefaultActivity = () => {
    setFormData({
      ...formData,
      defaultActivities: [
        ...formData.defaultActivities,
        { type: '', daysOffset: 7, priority: 'MEDIUM' },
      ],
    });
  };

  const removeDefaultActivity = (index: number) => {
    setFormData({
      ...formData,
      defaultActivities: formData.defaultActivities.filter((_, i) => i !== index),
    });
  };

  const updateDefaultActivity = (index: number, field: keyof DefaultActivity, value: any) => {
    const updated = [...formData.defaultActivities];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, defaultActivities: updated });
  };

  const moveDefaultActivity = (index: number, direction: 'up' | 'down') => {
    const updated = [...formData.defaultActivities];
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= updated.length) return;
    [updated[index], updated[target]] = [updated[target], updated[index]];
    setFormData({ ...formData, defaultActivities: updated });
  };

  const addDefaultAppointment = () => {
    setFormData({
      ...formData,
      defaultAppointments: [
        ...formData.defaultAppointments,
        { daysOffset: 14 },
      ],
    });
  };

  const removeDefaultAppointment = (index: number) => {
    setFormData({
      ...formData,
      defaultAppointments: formData.defaultAppointments.filter((_, i) => i !== index),
    });
  };

  const updateDefaultAppointment = (index: number, field: keyof DefaultAppointment, value: any) => {
    const updated = [...formData.defaultAppointments];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, defaultAppointments: updated });
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Sei sicuro di voler eliminare il tipo pratica "${name}"?`)) {
      return;
    }
    try {
      await deletePracticeType(id);
      toast.success('Tipo pratica eliminato');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Errore nell\'eliminazione');
    }
  };

  const handleToggleActive = async (type: any) => {
    try {
      await updatePracticeType(type.id, { isActive: !type.isActive });
      toast.success(`Tipo pratica ${type.isActive ? 'disattivato' : 'attivato'}`);
    } catch (error: any) {
      toast.error('Errore nella modifica');
    }
  };

  const sortedTypes = [...practiceTypes].sort((a, b) => a.order - b.order);
  const filteredTypes = useMemo(() => {
    if (!searchTerm.trim()) return sortedTypes;
    const query = searchTerm.toLowerCase();
    return sortedTypes.filter(
      (t) =>
        t.name?.toLowerCase().includes(query) ||
        t.code?.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query)
    );
  }, [sortedTypes, searchTerm]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/practices')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna alle pratiche
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tipi Pratiche</h1>
            <p className="mt-1 text-sm text-gray-500">
              Gestisci i tipi di pratiche disponibili (solo amministratori)
            </p>
          </div>
        </div>
        <Button onClick={() => {
          setEditingType(null);
          setFormData({
            name: '',
            code: '',
            description: '',
            icon: '',
            color: '',
            isActive: true,
            order: practiceTypes.length,
            defaultActivities: [],
            defaultAppointments: [],
            folderParentTemplate: FOLDER_TEMPLATE_DEFAULT,
            folderSubfolders: [],
          });
          setShowCreateModal(true);
        }}>
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Tipo
        </Button>
      </div>

      {/* Types List */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Caricamento tipi pratiche...</p>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedTypes.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Cerca per nome, codice o descrizione..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          )}
          {filteredTypes.length === 0 && sortedTypes.length > 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-gray-500 mb-4">Nessun risultato per la ricerca</p>
                <Button variant="outline" onClick={() => setSearchTerm('')}>
                  Cancella ricerca
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
          {filteredTypes.map((type) => (
            <Card key={type.id} className={!type.isActive ? 'opacity-60' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="flex items-center gap-2 mt-1">
                      <GripVertical className="h-5 w-5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-500">#{type.order}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{type.name}</h3>
                        {type.icon && <span className="text-xl">{type.icon}</span>}
                        <Badge variant={type.isActive ? 'default' : 'secondary'}>
                          {type.isActive ? 'Attivo' : 'Disattivato'}
                        </Badge>
                        {type.isDefault && (
                          <Badge variant="outline">Predefinito</Badge>
                        )}
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Codice:</span> {type.code}
                        </div>
                        {type.description && (
                          <div>{type.description}</div>
                        )}
                        {type.color && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Colore:</span>
                            <div
                              className="w-6 h-6 rounded-full border border-gray-300"
                              style={{ backgroundColor: type.color }}
                            />
                            <span>{type.color}</span>
                          </div>
                        )}
                        {type._count && (
                          <div>
                            <span className="font-medium">Pratiche associate:</span> {type._count.practices || 0}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleActive(type)}
                    >
                      {type.isActive ? 'Disattiva' : 'Attiva'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(type)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {!type.isDefault && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(type.id, type.name)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingType ? 'Modifica Tipo Pratica' : 'Nuovo Tipo Pratica'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Es: SCIA"
                />
              </div>
              <div>
                <Label htmlFor="code">Codice *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="Es: SCIA"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrizione del tipo pratica"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="icon">Icona (emoji)</Label>
                <Input
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="🏗️"
                />
              </div>
              <div>
                <Label htmlFor="color">Colore (HEX)</Label>
                <div className="flex gap-2">
                  <Input
                    id="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="#4F46E5"
                  />
                  <div
                    className="w-12 h-10 rounded border border-gray-300"
                    style={{ backgroundColor: formData.color || '#fff' }}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="order">Ordine</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-center pt-8">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Attivo</span>
                </label>
              </div>
            </div>

            {/* Attività Predefinite */}
            <div className="space-y-2">
              <Label>Attività Predefinite</Label>
              {formData.defaultActivities.length === 0 ? (
                <p className="text-sm text-gray-500">Nessuna attività predefinita</p>
              ) : (
                <div className="space-y-2">
                  {formData.defaultActivities.map((activity, index) => (
                    <div key={index} className="flex gap-2 items-center p-2 border rounded">
                      <div className="flex flex-col gap-0.5 mr-1">
                        <span className="text-xs font-mono text-gray-400 text-center">{index + 1}</span>
                        <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0" disabled={index === 0} onClick={() => moveDefaultActivity(index, 'up')}>
                          <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0" disabled={index === formData.defaultActivities.length - 1} onClick={() => moveDefaultActivity(index, 'down')}>
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="flex-1 grid grid-cols-[2fr_80px_100px] gap-2">
                        <div>
                          <Label className="text-xs">Tipo</Label>
                          <Combobox
                            options={activityTypes
                              .filter(t => t.isActive)
                              .sort((a, b) => a.order - b.order)
                              .map((type) => ({
                                value: type.code.trim(),
                                label: `${type.icon ? type.icon + ' ' : ''}${type.name.trim()}`,
                              }))}
                            value={activity.type?.trim()}
                            onValueChange={(value) => updateDefaultActivity(index, 'type', value)}
                            placeholder="Seleziona tipo..."
                            searchPlaceholder="Cerca tipo attività..."
                            emptyMessage="Nessun tipo trovato"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Giorni</Label>
                          <Input
                            type="number"
                            min="0"
                            value={activity.daysOffset}
                            onChange={(e) => updateDefaultActivity(index, 'daysOffset', parseInt(e.target.value) || 0)}
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Priorità</Label>
                          <Select
                            value={activity.priority || 'MEDIUM'}
                            onValueChange={(value) => updateDefaultActivity(index, 'priority', value)}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="LOW">Bassa</SelectItem>
                              <SelectItem value="MEDIUM">Media</SelectItem>
                              <SelectItem value="HIGH">Alta</SelectItem>
                              <SelectItem value="URGENT">Urgente</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDefaultActivity(index)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <Button type="button" variant="outline" size="sm" onClick={addDefaultActivity} className="w-full">
                <Plus className="h-4 w-4 mr-1" />
                Aggiungi attività
              </Button>
            </div>

            {/* Appuntamenti Predefiniti */}
            <div className="space-y-2">
              <Label>Appuntamenti Predefiniti</Label>
              {formData.defaultAppointments.length === 0 ? (
                <p className="text-sm text-gray-500">Nessun appuntamento predefinito</p>
              ) : (
                <div className="space-y-2">
                  {formData.defaultAppointments.map((appointment, index) => (
                    <div key={index} className="flex gap-2 items-start p-2 border rounded">
                      <div className="flex-1">
                        <Label className="text-xs">Giorni di offset</Label>
                        <Input
                          type="number"
                          min="0"
                          value={appointment.daysOffset}
                          onChange={(e) => updateDefaultAppointment(index, 'daysOffset', parseInt(e.target.value) || 0)}
                          className="h-8"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDefaultAppointment(index)}
                        className="h-8 w-8 p-0 mt-6"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <Button type="button" variant="outline" size="sm" onClick={addDefaultAppointment} className="w-full">
                <Plus className="h-4 w-4 mr-1" />
                Aggiungi appuntamento
              </Button>
            </div>

            {/* Struttura Cartelle */}
            <div className="space-y-2">
              <Label>Template Cartella Padre</Label>
              <Input
                value={formData.folderParentTemplate}
                onChange={(e) => setFormData({ ...formData, folderParentTemplate: e.target.value })}
                placeholder="{practiceNumber} - {clientName} - {interventionLocation} - {practiceTypeName}"
              />
              <p className="text-xs text-gray-500">
                Placeholder: {'{practiceNumber}'}, {'{clientName}'}, {'{interventionLocation}'}, {'{practiceTypeName}'}
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Sottocartelle</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setFormData({ ...formData, folderSubfolders: [...formData.folderSubfolders, ''] })}>
                  <Plus className="h-4 w-4 mr-1" />
                  Aggiungi
                </Button>
              </div>
              {formData.folderSubfolders.length === 0 ? (
                <p className="text-sm text-gray-500">Nessuna sottocartella configurata</p>
              ) : (
                <div className="space-y-2">
                  {formData.folderSubfolders.map((folder, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <Input
                        value={folder}
                        onChange={(e) => {
                          const updated = [...formData.folderSubfolders];
                          updated[index] = e.target.value;
                          setFormData({ ...formData, folderSubfolders: updated });
                        }}
                        placeholder="Nome sottocartella"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData({ ...formData, folderSubfolders: formData.folderSubfolders.filter((_, i) => i !== index) })}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annulla
            </Button>
            <Button
              onClick={editingType ? handleUpdate : handleCreate}
              disabled={!formData.name || !formData.code}
            >
              {editingType ? 'Aggiorna' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

